import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { leerClienteRecordado } from '../clienteActivo'
import { IconoAlerta, IconoCfdi, IconoContabilidadElectronica } from '../iconos'
import {
  api,
  type Despacho,
  type Notificacion,
  type ResumenDeCliente,
  type ResumenDeDescargaNocturna,
  type TipoNotificacion,
} from '../servicios/api'
import { TipDelDia } from '../ayuda/TipDelDia'

/**
 * Los cinco grados de cumplimiento del prototipo aprobado, del más tranquilo al más urgente.
 * La SPA solo distinguía tres (`SemaforoDeCumplimiento`), con lo que un rechazo del SAT y una
 * balanza vencida se veían igual pese a exigir cosas distintas del contador.
 */
const GRADOS = [
  // `etiqueta` va en la leyenda, donde hay renglón entero para explicarse; `chip` va en la
  // insignia de la tabla, donde compite con cuatro columnas más.
  { clave: 'aceptada', etiqueta: 'Aceptada', chip: 'Aceptada', color: 'grado-aceptada', insignia: 'vigente' },
  { clave: 'sinAcuse', etiqueta: 'Pendiente de acuse', chip: 'Sin acuse', color: 'grado-sin-acuse', insignia: 'neutra' },
  { clave: 'porVencer', etiqueta: 'Por vencer', chip: 'Por vencer', color: 'grado-por-vencer', insignia: 'aviso' },
  { clave: 'rechazada', etiqueta: 'Rechazada', chip: 'Rechazada', color: 'grado-rechazada', insignia: 'urgente' },
  { clave: 'vencida', etiqueta: 'Vencida', chip: 'Vencida', color: 'grado-vencida', insignia: 'vencido' },
] as const

type Grado = (typeof GRADOS)[number]['clave']

/**
 * Una fila del tablero. La pantalla anterior era una tabla de clientes, así que lo que un cliente
 * puede deber —la balanza del mes, el pago provisional (RF-1209), la balanza ajustada del ejercicio
 * y los envíos sin acuse o rechazados— compartía un solo renglón y un solo semáforo. Aquí cada una
 * es su propia fila con su propio plazo y su propia acción, que es lo que el contador atiende.
 */
interface Obligacion {
  clave: string
  clienteId: string
  razonSocial: string
  tipoPersona: string
  que: string
  /** De dónde sale el plazo, para quien pase el cursor por la obligación. */
  detalle?: string
  fechaLimite?: string
  grado: Exclude<Grado, 'aceptada'>
  accion: string
  seccion: string
}

/**
 * Cuánto se adelanta el tablero a la balanza ajustada. Es anual (periodo 13), así que sin un
 * horizonte aparecería todo el año en la lista de lo accionable y ahogaría a las mensuales.
 */
const DIAS_DE_AVISO_DEL_AJUSTE = 30

export function obligacionesDe(r: ResumenDeCliente, hoy: Date): Obligacion[] {
  const base = {
    clienteId: r.clienteId,
    razonSocial: r.razonSocial,
    tipoPersona: r.tipoPersona === 'Moral' ? 'PM' : 'PF',
  }
  const lista: Obligacion[] = []

  // Mensual: el semáforo lo calcula el servidor con el calendario de días hábiles (RF-806),
  // así que se respeta en vez de recalcularlo aquí con aritmética de días naturales.
  if (!r.proximaObligacion.yaCumplida && r.proximaObligacion.semaforo !== 'AlCorriente') {
    lista.push({
      ...base,
      clave: `${r.clienteId}-mensual`,
      que: `Balanza ${MESES[r.proximaObligacion.mes - 1]} ${r.proximaObligacion.anio}`,
      fechaLimite: r.proximaObligacion.fechaLimite,
      grado: r.proximaObligacion.semaforo === 'Vencido' ? 'vencida' : 'porVencer',
      accion: r.proximaObligacion.semaforo === 'Vencido' ? 'Generar y enviar' : 'Enviar',
      seccion: 'control-envios',
    })
  }

  // RF-1209 · RF-1606: el pago provisional del mes, con su plazo escalonado por el sexto dígito del
  // RFC. Se cumple con la declaración PRESENTADA (registrada en Declaraciones), y la fila dice cuál de
  // los dos pasos falta: sellar el papel o registrar la presentación. Sellar primero: sin papel no
  // hay qué presentar.
  const pago = r.pagoProvisional
  if (!pago.yaCumplido && pago.semaforo !== 'AlCorriente') {
    const sinPresentar = [!pago.ivaPresentada ? 'IVA' : null, pago.isrPresentada === false ? 'ISR' : null]
      .filter((impuesto): impuesto is string => impuesto !== null)
    // Solo se pide el papel de lo que falta presentar: una declaración ya presentada (quizá con un
    // cálculo hecho fuera) no necesita papel sellado aquí para estar cumplida.
    const sinPapel = sinPresentar
      .filter((impuesto) => (impuesto === 'IVA' ? !pago.papelDeIvaSellado : pago.papelDeIsrSellado === false))
    const comun = {
      ...base,
      clave: `${r.clienteId}-pago`,
      detalle: pago.origenDelPlazo,
      fechaLimite: pago.fechaLimite,
      grado: pago.semaforo === 'Vencido' ? 'vencida' as const : 'porVencer' as const,
    }
    lista.push(sinPapel.length > 0
      ? {
          ...comun,
          que: `Pago provisional ${MESES[pago.mes - 1]} ${pago.anio} · sin papel de ${sinPapel.join(' ni de ')} sellado`,
          accion: `Sellar papel de ${sinPapel[0]}`,
          seccion: sinPapel[0] === 'IVA' ? 'iva' : 'isr',
        }
      : {
          ...comun,
          que: `Pago provisional ${MESES[pago.mes - 1]} ${pago.anio} · falta registrar la presentación de ${sinPresentar.join(' y de ')}`,
          accion: 'Registrar presentación',
          seccion: 'declaraciones',
        })
  }

  // Anual (P-99, RF-802/RF-806): el servidor no expone su semáforo por separado, solo lo funde
  // en el del cliente, así que aquí se deriva de su propia fecha límite.
  if (!r.ajusteDeCierreCumplido) {
    const dias = diasHasta(r.limiteBalanzaAjustada, hoy)
    if (dias < 0 || dias <= DIAS_DE_AVISO_DEL_AJUSTE) {
      lista.push({
        ...base,
        clave: `${r.clienteId}-ajuste`,
        que: `Balanza ajustada · cierre ${r.anioDelEjercicioBalanzaAjustada}`,
        fechaLimite: r.limiteBalanzaAjustada,
        grado: dias < 0 ? 'vencida' : 'porVencer',
        accion: dias < 0 ? 'Generar y enviar' : 'Enviar',
        seccion: 'balanza-ajustada',
      })
    }
  }

  // Los contadores de envío son del cliente entero, no de un periodo: por eso van en su propia
  // fila y sin plazo, en vez de atribuirle un rechazo a la balanza del mes que toque.
  if (r.enviosRechazados > 0) {
    lista.push({
      ...base,
      clave: `${r.clienteId}-rechazo`,
      que: `${r.enviosRechazados} envío(s) rechazado(s) por el SAT`,
      grado: 'rechazada',
      accion: 'Corregir y reenviar',
      seccion: 'control-envios',
    })
  } else if (r.enviosPendientes > 0) {
    lista.push({
      ...base,
      clave: `${r.clienteId}-acuse`,
      que: `${r.enviosPendientes} envío(s) sin acuse`,
      grado: 'sinAcuse',
      accion: 'Registrar acuse',
      seccion: 'control-envios',
    })
  }

  return lista
}

/** El grado de un cliente es el peor de sus obligaciones; sin ninguna, está al corriente. */
export function gradoDe(obligaciones: Obligacion[]): Grado {
  for (const g of ['rechazada', 'vencida', 'sinAcuse', 'porVencer'] as const) {
    if (obligaciones.some((o) => o.grado === g)) return g
  }
  return 'aceptada'
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']


const fechaCorta = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`)
  return `${d.getDate()} ${MESES[d.getMonth()]}`
}

const fechaLarga = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('es-MX', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })

const diasHasta = (iso: string, hoy: Date) => {
  const limite = new Date(`${iso}T00:00:00`)
  const inicioDeHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
  return Math.round((limite.getTime() - inicioDeHoy.getTime()) / 86_400_000)
}

/**
 * El plazo en términos de hoy. El formato absoluto («vence 05 sep 2026») obliga a comparar
 * mentalmente contra el calendario para saber si corre prisa; el relativo lo dice de una vez y
 * deja la fecha exacta en el `title` para quien la necesite.
 */
export function plazoRelativo(iso: string, hoy: Date) {
  const dias = diasHasta(iso, hoy)
  if (dias < -1) return `venció hace ${-dias} días · ${fechaCorta(iso)}`
  if (dias === -1) return `venció ayer · ${fechaCorta(iso)}`
  if (dias === 0) return `vence hoy · ${fechaCorta(iso)}`
  if (dias === 1) return `mañana · ${fechaCorta(iso)}`
  return `en ${dias} días · ${fechaCorta(iso)}`
}

const ICONO_DE_NOTIFICACION: Record<TipoNotificacion, typeof IconoAlerta> = {
  VencimientoDeObligacion: IconoContabilidadElectronica,
  DescargaMasivaTerminada: IconoCfdi,
  ErrorDeServicioSat: IconoAlerta,
  CertificadoPorVencer: IconoAlerta,
  CancelacionDeCfdiRelevante: IconoAlerta,
  // RF-1609 · Un envío o una declaración presentada sin acuse: es del cumplimiento, como el vencimiento.
  AcusePendiente: IconoContabilidadElectronica,
}

const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })

/**
 * Cómo le fue a la descarga de madrugada en todo el despacho (RF-501, T11). Hasta aquí el
 * único rastro de la corrida era una notificación por cliente: para saber si funcionó había
 * que entrar cliente por cliente.
 *
 * No lleva el botón «Reintentar» del prototipo: no hay endpoint que dispare un reintento a
 * mano —lo hace el trabajador nocturno—, y un botón que no reintenta nada es peor que no
 * tenerlo. En su lugar, el enlace a los clientes, que es donde sí se puede actuar.
 */
export function TarjetaDeDescargaNocturna({ resumen }: { resumen: ResumenDeDescargaNocturna }) {
  const { clientesActivos, clientesConDescarga, solicitudesFallidas, solicitudesEnCola } = resumen
  const cubiertos = clientesActivos > 0 ? Math.round((clientesConDescarga / clientesActivos) * 100) : 0
  // Sin corrida no es lo mismo que una corrida con cero resultados: la primera puede ser que
  // el trabajador no se ejecutó, y decir «0 CFDI nuevos» lo taparía.
  const corrio = resumen.ultimaCorridaUtc != null

  return (
    <section className="tarjeta-descarga">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <h6 style={{ margin: 0 }}>Descarga nocturna</h6>
        <span className="sutil" style={{ margin: 0 }}>
          {corrio ? hora(resumen.ultimaCorridaUtc!) : 'sin corrida hoy'}
        </span>
      </div>

      {!corrio ? (
        <p className="sutil" style={{ marginTop: 0 }}>
          Hoy no ha corrido la descarga masiva del SAT (RF-501).
        </p>
      ) : (
        <>
          <p className="cifra" style={{ fontSize: 22 }}>
            {clientesConDescarga}<span> / {clientesActivos} clientes</span>
          </p>
          <div className="barra-semaforo" role="img"
            aria-label={`${cubiertos} % de los clientes activos con descarga hoy`}>
            <span className="grado-sin-acuse" style={{ flex: clientesConDescarga || 0 }} />
            <span className="descarga-faltante" style={{ flex: Math.max(clientesActivos - clientesConDescarga, 0) }} />
          </div>
          <p className="sutil" style={{ marginTop: 'var(--space-2)' }}>
            {resumen.cfdiNuevos.toLocaleString('es-MX')} CFDI nuevos · {resumen.duplicados} duplicados
          </p>
          {(solicitudesFallidas > 0 || solicitudesEnCola > 0) && (
            <p className={solicitudesFallidas > 0 ? 'error' : 'sutil'} role={solicitudesFallidas > 0 ? 'status' : undefined}>
              {solicitudesFallidas > 0 &&
                `${solicitudesFallidas} ${solicitudesFallidas === 1 ? 'solicitud quedó' : 'solicitudes quedaron'} sin completar. `}
              {solicitudesEnCola > 0 &&
                `${solicitudesEnCola === 1 ? 'Una sigue' : `${solicitudesEnCola} siguen`} en cola. `}
              <Link to="/clientes">Ver clientes</Link>
            </p>
          )}
        </>
      )}
    </section>
  )
}

/**
 * Tablero del despacho: los cuatro indicadores del periodo, los plazos que corren, el semáforo
 * de cumplimiento y la lista de obligaciones que se pueden atender ahora mismo.
 * SRS: RF-901 · RF-806 · RF-902 · RF-103, RN-08. Cédula: H-01.
 *
 * Era una tabla de ocho columnas orientada a clientes, donde los que estaban al día ocupaban
 * el mismo espacio que los que necesitaban algo. Ahora está orientada a obligaciones: solo
 * aparece lo accionable, con su acción al lado, y los que van al corriente se cuentan en el pie.
 */
export function Tablero() {
  const [hoy] = useState(() => new Date())
  const [despacho, setDespacho] = useState<Despacho | null>(null)
  const [resumen, setResumen] = useState<ResumenDeCliente[]>()
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([])
  const [descarga, setDescarga] = useState<ResumenDeDescargaNocturna | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // El tablero no depende de las notificaciones: si ese endpoint falla, la pantalla debe
    // cargar igual en vez de dejar al despacho sin su lista de pendientes.
    Promise.all([api.despachoActual(), api.tablero()])
      .then(([d, r]) => {
        setDespacho(d)
        setResumen(r)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'No se pudo cargar el tablero.'))
    api.notificaciones(true).then(setNotificaciones).catch(() => setNotificaciones([]))
    // T11: la descarga corre de madrugada y nadie la ve pasar. Igual que las notificaciones,
    // es información de apoyo: si no llega, el tablero carga sin la tarjeta.
    api.resumenDeDescargaNocturna().then(setDescarga).catch(() => setDescarga(null))
  }, [])

  const porCliente = useMemo(
    () => (resumen ?? []).map((r) => {
      const obligaciones = obligacionesDe(r, hoy)
      return { resumen: r, obligaciones, grado: gradoDe(obligaciones) }
    }),
    [resumen, hoy],
  )

  const obligaciones = useMemo(() => porCliente.flatMap((c) => c.obligaciones), [porCliente])

  /**
   * Un chip por fecha límite y tipo de persona: el mismo plazo lo comparten varios clientes.
   * Solo lo que está por venir — lo ya vencido tiene su renglón en la tabla y su cifra en los
   * indicadores, y repetirlo aquí llenaría la fila de chips con lo que ya no es un plazo.
   */
  const plazos = useMemo(() => {
    const porFecha = new Map<string, { fechaLimite: string; tipoPersona: string; cuantos: number }>()
    for (const o of obligaciones) {
      if (!o.fechaLimite || diasHasta(o.fechaLimite, hoy) < 0) continue
      const clave = `${o.fechaLimite}·${o.tipoPersona}`
      const ya = porFecha.get(clave)
      if (ya) ya.cuantos += 1
      else porFecha.set(clave, { fechaLimite: o.fechaLimite, tipoPersona: o.tipoPersona, cuantos: 1 })
    }
    return [...porFecha.values()].sort((a, b) => a.fechaLimite.localeCompare(b.fechaLimite)).slice(0, 4)
  }, [obligaciones, hoy])

  if (error) return <p className="error" role="alert">{error}</p>
  if (!despacho || !resumen) return <p>Cargando…</p>

  const cuenta = (g: Grado) => porCliente.filter((c) => c.grado === g).length
  const alCorriente = cuenta('aceptada')
  const cliente = leerClienteRecordado()

  const morales = resumen.filter((r) => r.tipoPersona === 'Moral').length
  const cfdiPendientes = resumen.reduce((suma, r) => suma + r.cfdiSinContabilizar, 0)
  const clientesConCfdi = resumen.filter((r) => r.cfdiSinContabilizar > 0).length
  const rechazos = resumen.reduce((suma, r) => suma + r.enviosRechazados, 0)
  const primerRechazo = resumen.find((r) => r.enviosRechazados > 0)

  // RF-1309: va aparte del semáforo, que es de la balanza; un movimiento del banco sin emparejar no
  // vence ninguna obligación, pero con más de 90 días casi siempre es un error que alguien debe ver.
  const conMovimientosAntiguos = resumen.filter((r) => r.movimientosBancariosAntiguos > 0)

  const conPlazo = obligaciones.filter((o) => o.fechaLimite)
  const estaSemana = conPlazo.filter((o) => diasHasta(o.fechaLimite!, hoy) <= 7)
  const vencidas = estaSemana.filter((o) => diasHasta(o.fechaLimite!, hoy) < 0).length
  const manana = estaSemana.filter((o) => diasHasta(o.fechaLimite!, hoy) === 1).length

  return (
    <section>
      {/* RF-1705 · El TIP del día: no sale si el usuario apagó los TIPs o ya lo cerró hoy. */}
      <TipDelDia />
      <header className="encabezado linea-base">
        <h1>Tablero</h1>
        <span className="encabezado-sub">
          {hoy.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} ·
          plan {despacho.plan} · {despacho.clientesActivos} de {despacho.maxClientes} clientes ·{' '}
          {despacho.usuariosActivos} de {despacho.maxUsuarios} usuarios
        </span>
        <span className="encabezado-acciones">
          {/* Las dos acciones viven bajo un cliente concreto; sin uno recordado no hay a dónde ir. */}
          {cliente ? (
            <>
              <Link className="boton" to={`/clientes/${cliente}/cfdi`}>Descargar CFDI</Link>
              <Link className="boton primario" to={`/clientes/${cliente}/polizas`}>Nueva póliza</Link>
            </>
          ) : (
            <Link className="boton primario" to="/clientes">Elegir cliente</Link>
          )}
        </span>
      </header>

      <div className="franja-kpi">
        <div>
          <div className="kicker">Clientes activos</div>
          <div className="kpi-cifra">{resumen.length}</div>
          <div className="kpi-pie">{morales} PM · {resumen.length - morales} PF</div>
        </div>
        <div>
          <div className="kicker">CFDI sin contabilizar</div>
          <div className="kpi-cifra">{cfdiPendientes}</div>
          <div className="kpi-pie">
            {clientesConCfdi === 0 ? 'ningún cliente con pendientes' : `en ${clientesConCfdi} cliente(s)`}
          </div>
        </div>
        <div>
          <div className="kicker">Obligaciones esta semana</div>
          <div className={`kpi-cifra${vencidas > 0 ? ' alerta' : ''}`}>{estaSemana.length}</div>
          <div className={`kpi-pie${vencidas > 0 ? ' alerta' : ''}`}>
            {vencidas} vencida(s) · {manana} vence(n) mañana
          </div>
        </div>
        <div>
          <div className="kicker">Envíos rechazados</div>
          <div className={`kpi-cifra${rechazos > 0 ? ' alerta' : ''}`}>{rechazos}</div>
          <div className="kpi-pie">{primerRechazo ? primerRechazo.razonSocial : 'sin rechazos del SAT'}</div>
        </div>
      </div>

      {plazos.length > 0 && (
        <div className="plazos" style={{ padding: 'var(--space-3) 0 0' }}>
          <span className="kicker">Próximos plazos</span>
          {plazos.map((p) => (
            <span
              key={`${p.fechaLimite}-${p.tipoPersona}`}
              className={`plazo-chip${diasHasta(p.fechaLimite, hoy) <= 1 ? ' urge' : ''}`}
              title={fechaLarga(p.fechaLimite)}
            >
              Balanza · {p.tipoPersona} — {plazoRelativo(p.fechaLimite, hoy)} · {p.cuantos} cliente(s)
            </span>
          ))}
        </div>
      )}

      <div className="rejilla-tablero" style={{ paddingTop: 'var(--space-4)' }}>
        <section data-recorrido="tablero-semaforo">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
            <h6 style={{ margin: 0 }}>Semáforo de cumplimiento</h6>
            <span className="encabezado-sub" style={{ fontSize: '11.5px' }}>
              Balanza de comprobación · {resumen.length} cliente(s)
            </span>
          </div>

          {resumen.length > 0 && (
            <>
              <div className="barra-semaforo" role="img" aria-label={
                GRADOS.filter((g) => cuenta(g.clave) > 0).map((g) => `${g.etiqueta}: ${cuenta(g.clave)}`).join(', ')
              }>
                {GRADOS.filter((g) => cuenta(g.clave) > 0).map((g) => (
                  <span key={g.clave} className={g.color} style={{ flex: cuenta(g.clave) }} />
                ))}
              </div>
              <div className="leyenda-semaforo">
                {GRADOS.map((g) => (
                  <span key={g.clave}>
                    <span className={`muestra ${g.color}`} aria-hidden="true" />
                    {g.etiqueta} · {cuenta(g.clave)}
                  </span>
                ))}
              </div>
            </>
          )}

          {resumen.length === 0 ? (
            <p className="sutil">Todavía no hay clientes activos.</p>
          ) : obligaciones.length === 0 ? (
            <p className="vacio">
              Ninguna obligación pendiente: los {alCorriente} clientes del despacho están al día.
            </p>
          ) : (
            <div className="tabla-envoltura">
              <table className="tabla obligaciones">
                <thead>
                  <tr><th>Cliente</th><th>Obligación</th><th>Plazo</th><th>Estado</th><th></th></tr>
                </thead>
                <tbody>
                  {obligaciones.map((o) => {
                    const g = GRADOS.find((x) => x.clave === o.grado)!
                    return (
                      <tr key={o.clave}>
                        <td><Link to={`/clientes/${o.clienteId}`}>{o.razonSocial}</Link></td>
                        <td className="secundaria" title={o.detalle}>{o.que} · {o.tipoPersona}</td>
                        <td className="secundaria" title={o.fechaLimite ? fechaLarga(o.fechaLimite) : undefined}>
                          {o.fechaLimite ? plazoRelativo(o.fechaLimite, hoy) : '—'}
                        </td>
                        <td><span className={`insignia ${g.insignia}`}>{g.chip}</span></td>
                        <td><Link to={`/clientes/${o.clienteId}/${o.seccion}`}>{o.accion}</Link></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {resumen.length > 0 && (
            <p className="pie-tabla">
              <span>
                {obligaciones.length} obligación(es) accionable(s) · {alCorriente} cliente(s) al corriente
              </span>
              {cliente && <Link to={`/clientes/${cliente}/control-envios`}>Control de envíos →</Link>}
            </p>
          )}

          {conMovimientosAntiguos.length > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <h6 style={{ margin: '0 0 6px' }}>Conciliación bancaria</h6>
              <ul aria-label="Clientes con movimientos bancarios sin emparejar de más de 90 días" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {conMovimientosAntiguos.map((r) => (
                  <li key={r.clienteId} style={{ marginBottom: 4 }}>
                    <Link to={`/clientes/${r.clienteId}/conciliacion-bancaria`}>{r.razonSocial}</Link>{' '}
                    <span className="insignia vencido">
                      {r.movimientosBancariosAntiguos} mov. bancario{r.movimientosBancariosAntiguos === 1 ? '' : 's'} sin emparejar · +90 días
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <aside aria-label="Notificaciones">
          {descarga && <TarjetaDeDescargaNocturna resumen={descarga} />}

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
            <h6 style={{ margin: 0 }}>Notificaciones</h6>
            <span className="insignia contador">{notificaciones.length}</span>
          </div>
          {notificaciones.length === 0 ? (
            <p className="sutil" style={{ marginTop: 0 }}>Sin avisos sin leer.</p>
          ) : (
            notificaciones.slice(0, 6).map((n) => {
              const Icono = ICONO_DE_NOTIFICACION[n.tipo] ?? IconoAlerta
              return (
                <div key={n.id} className="notificacion">
                  <Icono />
                  <div className="texto">
                    <strong>{n.titulo}</strong>
                    {n.clienteRazonSocial ? ` · ${n.clienteRazonSocial}` : ''} — {n.mensaje}
                    <div className="meta">
                      {hora(n.creadaEnUtc)}
                      {n.clienteId && <> · <Link to={`/clientes/${n.clienteId}`}>Ver cliente</Link></>}
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </aside>
      </div>
    </section>
  )
}
